/*
 * The StepSubscriptions component is the step in reseller SO flow enabling subscription selection.
 * 
 * This takes the following props:
 *   auth                     - authentication object
 *   customer                 - customer for which this SO is being created
 *   project                  - project for which this SO is being created
 *   forceParentQuery         - function to force parent component to refetch data.  TODO: refactor
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';

import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Button, Checkbox, Grid, FormControlLabel, 
         Paper, Tooltip, Typography,
         TableContainer, Table, TableBody, TableCell,
         TableHead, TableRow, TableSortLabel } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import { Add, HelpOutline } from '@mui/icons-material';

import AppBindStyles from '../../AppBindStyles';

import ConfirmationDialog from '../ui/ConfirmationDialog';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';
import SubscriptionTableRow from './SubscriptionTableRow';
import AppAutosuggest from '../app/AppAutosuggest';

import DeletePendingSubscriptionMutation from './DeletePendingSubscriptionMutation';
import RequestedBudgetAccountUpdateMutation from '../paymentsources/RequestedBudgetAccountUpdateMutation'
import RequestedBudgetAccountUnsetMutation from '../paymentsources/RequestedBudgetAccountUnsetMutation'
import RequestedBudgetProjectUpdateMutation from '../paymentsources/RequestedBudgetProjectUpdateMutation'
import RequestedBudgetWalletUnsetMutation from '../paymentsources/RequestedBudgetWalletUnsetMutation'
import AccountPreApproveUpdateMutation from './AccountPreApproveUpdateMutation'
import ProjectPreApproveUpdateMutation from './ProjectPreApproveUpdateMutation'

import AccountFetch from '../account/AccountFetch';
import StepSubscriptionEditDialog from './StepSubscriptionEditDialog';

const abEnvironment = new AbEnvironment();
const StepSubscriptionsQuery = graphql`
query StepSubscriptionsQuery($accountId: UUID!, $projectId: UUID) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountNum
    accountName
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    autoTopupActive
    autoTopupAmount
    autoTopupThreshhold
    preApproveSubs
    rPreApproveSubs
    budgetApprovedCount
    initialBalanceAmount
    rAutoTopupAmount
    rAutoTopupThreshold
    rMaxMonthlyBudget
    accountTByParentId {
    	accountName
      accountId
      contactFirstName
      contactLastName
      contactEmail
      accountStatus
      personaStatus
      initialBalanceAmount
      autoTopupActive
      autoTopupAmount
      autoTopupThreshhold
      userTsByAccountId (condition:{userStatus:0}) {
        nodes {
          email
          userStatus
        }
      }
    }
    projectTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        walletTByCustomerWalletId {
          walletId
          autoTopupAmount
          autoTopupThreshold
          maxMonthlyBudget
          initialBalanceAmount
          rAutoTopupAmount
          rAutoTopupThreshold
          rMaxMonthlyBudget
          rMaxCharge
        }
        walletTByResellerWalletId {
          walletId
          initialBalanceAmount
          autoTopupAmount
          autoTopupThreshold
          maxMonthlyBudget
        }
      }
    }
    customerAppTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        customerAppId
        customerAccountId
        accountTByCustomerAccountId {
          accountName
          contactFirstName
          contactLastName
          contactEmail
          accountType
          accountTByParentId {
            accountName
            contactFirstName
            contactLastName
            contactEmail
          }
          userTsByAccountId {
            nodes {
              accountLinkTsByUserId {
                totalCount
              }
            }
          }
          paymentSourceTsByAccountId {
            totalCount
          }
        }
        appStatus
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        paymentOrder
        passThroughFees
        bcId
        bindCardTByBcId {
          bcStatus
        }
        sharedEmail
        pwd
        showSubscription
        rAppMarkupPercentage
        rPaymentOrder
        rPassThroughFees
        appId
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
  paper: {
    padding: theme.spacing(3),
    textAlign: 'left',
    // maxWidth: 980
  }
});

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderBottom: 'none',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const StyledTableCellNarrow = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(0),
  paddingRight: theme.spacing(1),
  width: 32
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function subPaymentOrder(sub) {
  if (sub.rPaymentOrder != null && sub.rPaymentOrder !== sub.paymentOrder) {
    return sub.rPaymentOrder;
  }
  return sub.paymentOrder;
};

class StepSubscriptions extends React.Component {
  
  state = {
    sortBy: 'status',
    sortOrder: 'asc',

    showAllSubscriptions: false,
    showSpinner: false,

    nextDisabled: false,

    accepted:    false,
    deleteConfirm: false, 
    subscriptionToDelete: null,
    showSubscriptionEdit: false, 
    subscriptionToEdit: null,
    fetchCount: 0,
    rPreApproveSubs: null,
    bcUser: {
      userId:         null,
      usingAccountId: null,
      accountName:    null,
      description:    null,
      lastName:       null,
      firstName:      null,
      email:          null,
      phone:          null,
      address1:       null,
      address2:       null,
      city:           null,
      state:          null,
      country:        null,
      postalCode:     null,
      marqetaUid:     null,
      mCardToken:     null,
    },
    pbcUser: {
      userId:         null,
      usingAccountId: null,
      accountName:    null,
      description:    null,
      lastName:       null,
      firstName:      null,
      email:          null,
      phone:          null,
      address1:       null,
      address2:       null,
      city:           null,
      state:          null,
      country:        null,
      postalCode:     null,
      marqetaUid:     null,
      mCardToken:     null,
    }
  }

  componentDidMount() {
    //
    // Hydrate the bcUser here.
    // - if an account_id has been passed, this will be the using account for the
    //   subscription.  If the using account is not the same as the current user
    //   account (provisioner), lookup address information from the db.  Otherwise,
    //   set from the account info in the context
    //

    // Start with the provisioning account
    let thePbcUser = {
      userId:         abEnvironment.getUserAttribute('u_id'),
      usingAccountId: abEnvironment.getUserAttribute('a_id'),
      accountName:    abEnvironment.getUserAttribute('a_accountName'),
      description:    abEnvironment.getUserAttribute('a_description'),
      lastName:       abEnvironment.getUserAttribute('a_lastName'),
      firstName:      abEnvironment.getUserAttribute('a_firstName'),
      email:          abEnvironment.getUserAttribute('a_email'),
      phone:          abEnvironment.getUserAttribute('a_phone'),
      address1:       abEnvironment.getUserAttribute('a_address1'),
      address2:       (abEnvironment.getUserAttribute('a_address2') ? abEnvironment.getUserAttribute('a_address2') : ''),
      city:           abEnvironment.getUserAttribute('a_city'),
      state:          abEnvironment.getUserAttribute('a_state'),
      country:        abEnvironment.getUserAttribute('a_country'),
      postalCode:     abEnvironment.getUserAttribute('a_postalCode'),
      marqetaUid:     null,
      mCardToken:     null,
    };
     
    let usingAccountId = this.props.customer.accountId;
    if (usingAccountId != null && usingAccountId !== thePbcUser.usingAccountId) {
      // Lookup the using account id
      AccountFetch.fetch(usingAccountId).toPromise()
      .then(af => {
        let theBcUser = {
          userId:         abEnvironment.getUserAttribute('u_id'), // Still use provisioner user id
          usingAccountId: usingAccountId,
          accountName:    af.accountTByAccountId.accountName,
          description:    af.accountTByAccountId.description,
          lastName:       af.accountTByAccountId.contactLastName,
          firstName:      af.accountTByAccountId.contactFirstName,
          email:          af.accountTByAccountId.contactEmail,
          phone:          af.accountTByAccountId.contactPhone,
          address1:       af.accountTByAccountId.address1,
          address2:       af.accountTByAccountId.address2,
          city:           af.accountTByAccountId.city,
          state:          af.accountTByAccountId.state,
          country:        af.accountTByAccountId.country,
          postalCode:     af.accountTByAccountId.postalCode,
          marqetaUid:     null,
          mCardToken:     null,
        };
        this.setState({bcUser: theBcUser, pbcUser: thePbcUser});
      })
      .catch(err => {
        this.setState({bcUser: thePbcUser, pbcUser: thePbcUser});
      });
    } else {
      this.setState({bcUser: thePbcUser, pbcUser: thePbcUser});
    }

    if (localStorage.getItem('soOpenSubscriptionsByDefault')) {
      localStorage.removeItem('soOpenSubscriptionsByDefault');
      this.setState({showAllSubscriptions: true});
    }

    const { callPartnerApi } = this.props.auth;


    let payload = {
      operation: "getAppBindAddress",
      params: {}
    }
    
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    ).then(resp => {
      return resp.json();
    })
    .then(response => {
      this.setState({appBindAddress: response});
    });
  }

  isBackDisabled() {
    if (this.props.activeStep === 0) {
      return true;
    }

    return false;
  }

  isNextDisabled(queryProps) {
    if (this.state.nextDisabled === true) {
      return true;
    }

    if (this.props.activeStep === this.props.assignedStep) {
      return false;
    }
    return false;
  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;

    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  confirmDelete = (customerApp) => {
    this.setState({deleteConfirm: true, subscriptionToDelete: customerApp});
  }

  showSubscriptionEdit = (customerApp) => {
    this.setState({showSubscriptionEdit: true, subscriptionToEdit: customerApp});
  }

  closeSubscriptionEdit() {
    this.setState({showSubscriptionEdit: false, subscriptionToEdit: null, showAllSubscriptions: true});
  }

  confirmSubscriptionEdit() {
    let fetchCount = this.state.fetchCount;
    fetchCount++;
    //todo show all subscriptions not keeping accordian open.
    this.setState({fetchCount: fetchCount, showSubscriptionEdit: false, subscriptionToEdit: null, showAllSubscriptions: true});
  }

  handleApproveSubsChange = (props, rPreApproveSubs) => {
   
    if (this.state.rPreApproveSubs !== 0 && this.state.rPreApproveSubs !== 1 ) {
      //state is undefined.  Depending on state of props, we then bootstrap state with either 0 or 1 (opposite of DB value)
      if (rPreApproveSubs == null || rPreApproveSubs === 0) {
        this.setState({rPreApproveSubs : 1 });
      } else {
        this.setState({rPreApproveSubs : 0 });
      }
    } else if (this.state.rPreApproveSubs === 0) {
      this.setState({rPreApproveSubs : 1 });
    } else {
      this.setState({rPreApproveSubs : 0 });
    }
  };

  toggleOrder = (ob) => {
    if (ob === this.state.sortBy) {
      if (this.state.sortOrder === 'desc') {
        this.setState({sortOrder: 'asc'});
      } else {
        this.setState({sortOrder: 'desc'});
      }
    } else {
      this.setState({sortBy: ob, sortOrder: 'asc'});
    }
  } 

  render() {
    const { logError } = this.props.auth;

    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }

    let usingAccountId = this.props.customer.accountId;
    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');

    // Was a project passed in?  If so, the subscription is for a project.
    // If not, legacy case.
    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    let usingAccountName = ((this.state.bcUser.accountName) ? this.state.bcUser.accountName : 'Customer');

    // if there is a local storage value with the app, pre-populate
    let app = localStorage.getItem('soAppParm');
    if (app) {
      localStorage.removeItem('soAppParm');
    }

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={StepSubscriptionsQuery}
        variables={{accountId: usingAccountId, projectId: projectId, fetchCount: this.state.fetchCount}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {

            let theAccount = props.accountTByAccountId;

            // Project if relevant
            let theProject = null;
            if (theAccount.projectTsByCustomerAccountId.totalCount > 0) {
              theProject = theAccount.projectTsByCustomerAccountId.nodes[0];
            }

            // Set up an array of all active and pending subscriptions (including suspended)
            let allSubscriptions = [];
            for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
              let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
              //exclude cancelled subs
              if (sub.appStatus !== 2) {
                allSubscriptions.push(sub);
              }
            }

            /*
             * In display only mode, we're going to show just pending and changed subscriptions, so we'll
             * put those in a separate array - changedOrPendingSubscriptions.
             * 
             * allSubscriptions will be sorted in edit mode, where the user can choose a number of sorting options.
             * changedOrPendingSubscriptions will sort alphabetically.
             * 
             * We'll also categorize subscriptions so that we can display a reasonable message.
             */
            
            let unchangedCount = 0;
            let changedCount = 0;
            let pendingCount = 0;
            let existingCount = 0;
            let customerPaidCount = 0;
            let resellerPaidCount = 0;
            let changedOrPendingCount = 0;

            let changedOrPendingSubscriptions = [];
            for (let i = 0; i < allSubscriptions.length; i++) {
              let sub = allSubscriptions[i];
              let po = subPaymentOrder(sub); // If rPaymentOrder is set, use that.  Otherwise, use paymentOrder

              if (po === 0 || po === 1 || po === 2) {
                if (sub.bindCardTByBcId.bcStatus === -1) {
                  pendingCount++;
                  changedOrPendingSubscriptions.push(sub);
                } else {
                  if (sub.rPassThroughFees !== null || (sub.rAppMarkupPercentage !== null && sub.rAppMarkupPercentage !== sub.appMarkupPercentage) || (sub.rPaymentOrder != null && sub.rPaymentOrder !== sub.paymentOrder && ! (sub.rPaymentOrder === 0 && sub.paymentOrder === 1))) {
                    changedCount++;
                    changedOrPendingSubscriptions.push(sub);
                  } else {
                    unchangedCount++;
                  }
                }
                // if customer paid, add to that count
                // - use sortPaymentOrder to account for changes (sum and sort by the new value)
                if (po === 0 || po === 1) {
                  customerPaidCount++;
                } else if (po === 2) {
                  resellerPaidCount++;
                }
              } else if (po === 4 || sub.bindCardTByBcId.bcStatus === 4) {
                if (sub.pwd == null) {
                  changedOrPendingSubscriptions.push(sub);
                  pendingCount++;
                } else {
                  unchangedCount++;
                }
              }
            }
            existingCount = unchangedCount + changedCount;
            changedOrPendingCount = changedOrPendingSubscriptions.length;

            let changedOrPendingOrPreApprovedCount = changedOrPendingCount;
            if (this.state.rPreApproveSubs === 1) {
              changedOrPendingOrPreApprovedCount = changedOrPendingCount + 1;
            }

            // console.log('allSubscriptionCount: ' + allSubscriptions.length + ', existing: ' + existingCount + ', unchanged ' + unchangedCount + ', changedOrPendingCount: ' + changedOrPendingCount + ', changed ' + changedCount + ', pending ' + pendingCount);

            // Setup preApproval information used in both view modes
            let preApproveSubs = theAccount.preApproveSubs;
            let rPreApproveSubs = theAccount.rPreApproveSubs;
            if (theProject != null) {
              preApproveSubs = theProject.preApproveSubs;
              rPreApproveSubs = theProject.rPreApproveSubs;
            }

            if (!displayOnly) {
              //
              // Sort subscriptions by the user's selected sort by / direction.
              //
              if (allSubscriptions.length > 1) {
                // Sort depends on sorting order in state
                if (this.state.sortBy === 'model') {
                  // Sorting by model means paymentOrder
                  // But we're going to sort alpha by the decoded names, so "Ask customer for access" (4), then "Expense to customer" (0 or 1), then "I will pay" (2) 
                  if (this.state.sortOrder === 'desc') {
                    allSubscriptions.sort(function(a, b) {
                      /* Sort by model, then app name alpha */
                      let apo = subPaymentOrder(a);
                      let aModel = apo;
                      if (apo === 4) {
                        aModel = 0;
                      } else if (apo === 5) {
                        aModel = 1;
                      } else if (a.bindCardTByBcId.bcStatus === 4) {
                        aModel = 0;
                      } else if (apo === 2) {
                        aModel = 2;
                       } else if (apo === 0 || apo === 1) {
                        aModel = 3;
                      }

                      let bpo = subPaymentOrder(b);
                      let bModel = bpo;
                      if (bpo === 4) {
                        bModel = 0;
                      } else if (bpo === 5) {
                        bModel = 1;
                      } else if (b.bindCardTByBcId.bcStatus === 4) {
                        bModel = 0;
                      } else if (bpo === 2) {
                        bModel = 2;
                      } else if (bpo === 0 || bpo === 1) {
                        bModel = 3;
                      }

                      if (aModel < bModel) { return 1; }
                      if (aModel > bModel) { return -1; }

                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  } else {
                    allSubscriptions.sort(function(a, b) {
                      /* Sort by model, then app name alpha */
                      // Treat 0 and 1 as the same
                      let apo = subPaymentOrder(a);
                      let aModel = apo;
                      if (apo === 4) {
                        aModel = 0;
                      } else if (apo === 5) {
                        aModel = 1;
                      } else if (a.bindCardTByBcId.bcStatus === 4) {
                        aModel = 0;
                      } else if (apo === 2) {
                        aModel = 2;
                       } else if (apo === 0 || apo === 1) {
                        aModel = 3;
                      }

                      let bpo = subPaymentOrder(b);
                      let bModel = bpo;
                      if (bpo === 4) {
                        bModel = 0;
                      } else if (bpo === 5) {
                        bModel = 1;
                      } else if (b.bindCardTByBcId.bcStatus === 4) {
                        bModel = 0;
                      } else if (bpo === 2) {
                        bModel = 2;
                      } else if (bpo === 0 || bpo === 1) {
                        bModel = 3;
                      }

                      if (aModel < bModel) { return -1; }
                      if (aModel > bModel) { return 1; }
                      
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  }
                } else if (this.state.sortBy === 'status') {
                  /* 
                  * Status means is it released or pending
                  * As we're now considering auth only, this is more complex. 
                  * - If paymentOrder is 0, 1, or 2, bcStatus of -1 means pending
                  * - If paymentOrder is 4, a null password means pending
                  * 
                  * So we'll be constructing a synthetic status value and sorting on that.
                  */
                  if (this.state.sortOrder === 'desc') {
                    allSubscriptions.sort(function(a, b) {
                      /* Create the synthetic status */
                      let aStatus = 0;
                      let bStatus = 0;

                      let apo = subPaymentOrder(a);
                      if (apo >= 0 && apo <= 2) {
                        if (a.bindCardTByBcId.bcStatus === -1) {
                          aStatus = 1;
                        }
                      } else if (apo === 4 || apo === 5 || a.bindCardTByBcId.bcStatus === 4) {
                        if (a.pwd == null) {
                          aStatus = 1;
                        }
                      }

                      let bpo = subPaymentOrder(b);
                      if (bpo >= 0 && bpo <= 2) {
                        if (b.bindCardTByBcId.bcStatus === -1) {
                          bStatus = 1;
                        }
                      } else if (bpo === 4 || bpo === 5 || b.bindCardTByBcId.bcStatus === 4) {
                        if (b.pwd == null) {
                          bStatus = 1;
                        }
                      }

                      /* Sort by status, then app name alpha */
                      if (aStatus < bStatus) { return 1; }
                      if (bStatus < aStatus) { return -1; }
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  } else {
                    allSubscriptions.sort(function(a, b) {
                      /* Create the synthetic status */
                      let aStatus = 0;
                      let bStatus = 0;

                      let apo = subPaymentOrder(a);
                      if (apo >= 0 && apo <= 2) {
                        if (a.bindCardTByBcId.bcStatus === -1) {
                          aStatus = 1;
                        }
                      } else if (apo === 4 || apo === 5 || a.bindCardTByBcId.bcStatus === 4) {
                        if (a.pwd == null) {
                          aStatus = 1;
                        }
                      }

                      let bpo = subPaymentOrder(b);
                      if (bpo >= 0 && bpo <= 2) {
                        if (b.bindCardTByBcId.bcStatus === -1) {
                          bStatus = 1;
                        }
                      } else if (bpo === 4 || bpo === 5 || b.bindCardTByBcId.bcStatus === 4) {
                        if (b.pwd == null) {
                          bStatus = 1;
                        }
                      }

                      /* Sort by status, then app name alpha */
                      if (aStatus < bStatus) { return -1; }
                      if (bStatus < aStatus) { return 1; }
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  }                  
                } else {
                  // Sort by app name
                  if (this.state.sortOrder === 'desc') {
                    allSubscriptions.sort(function(a, b) {
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return 1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return -1; }
                      return 0;
                    });
                  } else {
                    allSubscriptions.sort(function(a, b) {
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  }
                }
              }

              //
              // Subscription block title
              //
              let getSubscriptionTitle = "Subscriptions for my customer";
              if (this.state.bcUser.accountName) {
                getSubscriptionTitle = "Subscriptions for " + this.state.bcUser.accountName;
              }

              //
              // Subscription block tooltip
              //
              let getSubscriptionTooltip = `
              You can request access from your customer to subscriptions they already have, or
              sign up for subscriptions on behalf of your customer.  If you're purchasing a subscription for your customer,
              you can bill it directly them through AppBind (with a markup or discount), or expense it to yourself.
              `;
        
              //
              // Subscription block detail
              //
              /*
              let getSubscriptionDetail = "You haven't added any subscriptions.";
              if (allSubscriptions.length > 0) {
                getSubscriptionDetail = "There are " + existingCount + " existing subscriptions";
                if (changedCount > 0) { 
                  getSubscriptionDetail = getSubscriptionDetail + " (" + changedCount + " with changes)";
                }
                getSubscriptionDetail = getSubscriptionDetail + " and " + pendingCount + " new subscriptions.";
              }
              */

              let getSubscriptionDetail = "You haven't added any subscriptions.";
              if (allSubscriptions.length > 0) {
                if (existingCount === 1) {
                  getSubscriptionDetail = "There is " + existingCount + " existing subscription";
                } else {
                  getSubscriptionDetail = "There are " + existingCount + " existing subscriptions";
                }
                if (changedOrPendingCount === 0) {
                  getSubscriptionDetail = getSubscriptionDetail + " and no new or changed subscriptions.";
                } else {
                  if (changedCount > 0) {
                    getSubscriptionDetail = getSubscriptionDetail + " (" + changedCount + " with changes)";
                  }
                  if (pendingCount > 0) {
                    if (pendingCount === 1) {
                      getSubscriptionDetail = getSubscriptionDetail + " and 1 new subscription.";
                    } else {
                      getSubscriptionDetail = getSubscriptionDetail + " and " + pendingCount + " new subscriptions.";
                    }
                  } else {
                    getSubscriptionDetail = getSubscriptionDetail + ".";
                  }
                }
              }

  
              //
              // Pre-authorization message.
              // This also needs to account for whether the setting is
              // project based or account based.
              //
              let preAuthorizationMessage = 'Ask your customer';
              if (this.state.bcUser.accountName) {
                preAuthorizationMessage = 'Ask ' + this.state.bcUser.accountName;
              }
              preAuthorizationMessage = preAuthorizationMessage + ' for authorization to sign up for additional subscriptions as needed.';

              let preAuthorizationTooltip = "This setting asks your customer to pre-approve additional subscriptions.  This means that in the future if you're purchasing subscriptions for your customer, you won't need to wait for an approval before you can set up a paid subscription on their behalf.";
  
              let preAuthorizedDisabled = true;
              if (customerPaidCount > 0) {
                preAuthorizedDisabled = false;
              }

              // Prep a list of emails for passing into AppAutosuggest and StepSubscriptionEditDialog
              // - AppAutosuggest is for adding a new subscription, so the defaults are needed
              // - StepSubscriptionEditDialog is editing an existing subscription, but if this
              //   is changing from requesting credentials to requesting an invite, we'll need the
              //   defaults.
              //
              let inviteEmails = [];
              let defaultEmail = null;

              if (theAccount.accountTByParentId != null) {
                let resellerAccount = theAccount.accountTByParentId;
                inviteEmails.push(resellerAccount.contactEmail);
                defaultEmail = resellerAccount.contactEmail;

                if (resellerAccount.userTsByAccountId != null) {
                  for (let i = 0; i < resellerAccount.userTsByAccountId.nodes.length; i++) {
                    let user = resellerAccount.userTsByAccountId.nodes[i];
                    if (user.userStatus === 0) {
                      // Is it already there?
                      let emailAlreadyAdded = false;
                      for (let j=0; j<inviteEmails.length; j++) {
                        if (inviteEmails[j] === user.email) {
                          emailAlreadyAdded = true;
                          break;
                        }
                      }

                      if (emailAlreadyAdded === false) {
                        inviteEmails.push(user.email);
                      }
                    }
                  }
                }
              }

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Grid container spacing={0}>
                            <Grid item>
                              <Typography align="left" variant="h6">{getSubscriptionTitle}</Typography>
                            </Grid>                  
                            <Grid item>
                              <Tooltip placement='bottom' title={getSubscriptionTooltip}>
                                <HelpOutline fontSize="small" style={{verticalAlign: "-32%", marginLeft: '4px'}}/>
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography align="left" variant="body2">{getSubscriptionDetail}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {allSubscriptions.length >= 0 &&
                            <Grid item xs={12}>
                              <TableContainer>
                                <StyledTable>
                                  <TableHead>
                                    <TableRow>
                                      <StyledTableCell sx={{ borderTopLeftRadius: 8}}>
                                        {allSubscriptions.length > 1 && pendingCount > 0 &&
                                        <TableSortLabel
                                          active={this.state.sortBy === 'status'}
                                          direction={this.state.sortOrder}
                                          onClick={() => this.toggleOrder('status') }>
                                          <Add/>
                                        </TableSortLabel>}
                                        {allSubscriptions.length > 1 && pendingCount === 0 && changedCount > 0 &&
                                        <Add style={{ paddingTop: 5}}/>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allSubscriptions.length > 1 &&
                                        <TableSortLabel
                                          active={this.state.sortBy === 'appname'}
                                          direction={this.state.sortOrder}
                                          onClick={() => this.toggleOrder('appname') }>
                                          Subscription
                                        </TableSortLabel>}
                                        {allSubscriptions.length === 1 &&
                                        <React.Fragment>Subscription</React.Fragment>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allSubscriptions.length > 1 &&
                                        <TableSortLabel
                                          active={this.state.sortBy === 'model'}
                                          direction={this.state.sortOrder}
                                          onClick={() => this.toggleOrder('model') }>
                                          Sharing model
                                        </TableSortLabel>}
                                        {allSubscriptions.length === 1 &&
                                        <React.Fragment>Sharing model</React.Fragment>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allSubscriptions.length >= 1 &&
                                        <React.Fragment>Terms</React.Fragment>}
                                      </StyledTableCell>
                                      {allSubscriptions.length === 0 &&
                                      <React.Fragment>
                                        <StyledTableCellNarrow sx={{ height: 8}}/>
                                        <StyledTableCellNarrow sx={{ height: 8, borderTopRightRadius: 8}}/>
                                      </React.Fragment>}
                                      {allSubscriptions.length > 0 &&
                                      <React.Fragment>
                                        <StyledTableCellNarrow/>
                                        <StyledTableCellNarrow sx={{ borderTopRightRadius: 8}}/>
                                      </React.Fragment>}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {allSubscriptions.map(n => {
                                      return (
                                        <SubscriptionTableRow 
                                          key={n.customerAppId}
                                          subscription={n}
                                          styledTableRow={StyledTableRow}
                                          styledTableCell={StyledTableCell}
                                          styledTableCellNarrow={StyledTableCellNarrow}
                                          editable={true}
                                          confirmDelete={this.confirmDelete}
                                          showSubscriptionEdit={this.showSubscriptionEdit}
                                          showModel={true}
                                        />
                                      );
                                    })}
                                    <AppAutosuggest
                                      auth={this.props.auth}
                                      colSpan={6}
                                      appType="subscription"
                                      addSubscription={this.addSubscription}
                                      addAuthOnlySubscription={this.addAuthOnlySubscription}
                                      usingAccountId={usingAccountId}
                                      usingAccountName={usingAccountName}
                                      provisioningAccountId={provisioningAccountId}
                                      setDisableNext={this.setDisableNext}
                                      defaultApp={app}
                                      placeholder="Add a subscription to the project"
                                      suggestDefaults={false}
                                      autoFocus={true}
                                      inviteEmails={inviteEmails}
                                      defaultEmail={defaultEmail}
                                    />
                                  </TableBody>
                                </StyledTable>
                              </TableContainer>
                            </Grid>}
                          </Grid>
                        </Grid>

                        {preApproveSubs === 0 && preAuthorizedDisabled === false &&
                        <Grid item xs={12}>
                          <Tooltip title={preAuthorizationTooltip}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={(this.state.rPreApproveSubs === null) ? (rPreApproveSubs === 1) : (this.state.rPreApproveSubs === 1)} 
                                  onChange={() => {this.handleApproveSubsChange(props, rPreApproveSubs)} } 
                                  disabled={preAuthorizedDisabled}
                                />
                              }
                              label={preAuthorizationMessage}
                            />
                          </Tooltip>
                        </Grid>}
                        {preApproveSubs === 1 && 
                        <Grid item xs={12}>
                          <Typography style={AppBindStyles.boldLabel}>Additional subscriptions as needed.</Typography>
                        </Grid>}
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Dialogs */}
                  {this.state.showSubscriptionEdit === true &&
                  <StepSubscriptionEditDialog
                    auth={this.props.auth}
                    open={this.state.showSubscriptionEdit}
                    customerAppId={this.state.subscriptionToEdit.customerAppId}
                    inviteEmails={inviteEmails}
                    defaultEmail={defaultEmail}
                    onCancel={() => {this.closeSubscriptionEdit() }}
                    onConfirm={() => {this.confirmSubscriptionEdit() }}
                  />}
                  <ConfirmationDialog
                    open={this.state.deleteConfirm}
                    onClose={() => { this.setState({deleteConfirm:false, subscriptionToDelete: null});}}
                    onConfirm={() => {this.deleteSubscription(theAccount, theProject, customerPaidCount, resellerPaidCount) }}
                    onCancel={() => { }}
                    title="Remove subscription?"
                    cancelBtn="Cancel"
                    okBtn="Ok"
                    irreversibleAction={true}
                  >
                    If you proceed, this subscription will be removed.
                  </ConfirmationDialog>  

                  {this.props.activeStep === this.props.assignedStep &&
                  <Grid item xs={12}>
                    <Button
                      disabled={this.isBackDisabled()}
                      color="primary" variant="outlined"
                      onClick={() => this.props.handleBack()}
                      style={AppBindStyles.bottomButton}>
                      Back
                    </Button>

                    {!this.showSpinner() &&        
                    <Button
                      color="primary" variant="contained"
                      disabled={this.isNextDisabled(props)}
                      onClick={() => { this.handleNext(changedOrPendingOrPreApprovedCount, theProject, theAccount);}}
                      style={AppBindStyles.bottomButton}>
                        Next
                    </Button>}
                    {this.showSpinner() &&
                    <Button color="primary" 
                            variant="contained"
                            style={AppBindStyles.bottomButton}>
                        <SavingSpinner/>
                    </Button>}

                  </Grid>} 
                </Grid>       
              );
            } else {
              //
              // Display-only mode
              //

              //
              // Pre-authorization message
              // - this should show if pre-approval is already on, or is being switched on.
              //
              let showPreApprove = false;

              let messagePrefix = 'Request pre-approval to add ';
              if (allSubscriptions.length > 0) {
                messagePrefix = 'Request pre-approval to add additional ';
              }
              let preAuthorizationMessage = '';
              if (this.state.rPreApproveSubs === 1) {
                showPreApprove = true;
                preAuthorizationMessage = messagePrefix + ' subscriptions as needed.';
              } else if (this.state.rPreApproveSubs === 0) {
                showPreApprove = false;
              } else if (rPreApproveSubs === 1) {
                showPreApprove = true;
                preAuthorizationMessage = messagePrefix + ' subscriptions as needed.';
              } else if (rPreApproveSubs === 0) {
                showPreApprove = false;
              } else if (preApproveSubs === 1) {
                showPreApprove = true;
                preAuthorizationMessage = messagePrefix + ' subscriptions as needed.';
              }

              //
              // Subscription block title
              //
              let getSubscriptionTitle = "Subscription changes";
              /*
              if (this.state.bcUser.accountName) {
                getSubscriptionTitle = "Subscriptions changes for " + this.state.bcUser.accountName;
              }
              */

              //
              // Subscription block detail
              //
              let getSubscriptionDetail = "You haven't added any subscriptions.";
              if (allSubscriptions.length > 0) {
                if (existingCount === 1) {
                  getSubscriptionDetail = "There is " + existingCount + " existing subscription";
                } else {
                  getSubscriptionDetail = "There are " + existingCount + " existing subscriptions";
                }
                if (changedOrPendingCount === 0) {
                  getSubscriptionDetail = getSubscriptionDetail + " and no new or changed subscriptions.";
                } else {
                  if (changedCount > 0) {
                    getSubscriptionDetail = getSubscriptionDetail + " (" + changedCount + " with changes)";
                  }
                  if (pendingCount > 0) {
                    if (pendingCount === 1) {
                      getSubscriptionDetail = getSubscriptionDetail + " and 1 new subscription.";
                    } else {
                      getSubscriptionDetail = getSubscriptionDetail + " and " + pendingCount + " new subscriptions.";
                    }
                  } else {
                    getSubscriptionDetail = getSubscriptionDetail + ".";
                  }
                }
              }

              // Sort changedOrPendingSubscriptions by app name asc.
              if (changedOrPendingSubscriptions.length > 1) {
                changedOrPendingSubscriptions.sort(function(a, b) {
                  if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                  if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                  return 0;
                });
              }

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={0}>
                      <Grid item>
                        <Typography align="left" variant="h6">{getSubscriptionTitle}</Typography>
                      </Grid>                  
                      <Grid item xs={12}>
                        <Typography align="left" variant="body2">{getSubscriptionDetail}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {changedOrPendingCount > 0 &&
                  <Grid item xs={12}>
                    <TableContainer>
                      <StyledTable>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                            <StyledTableCell>Subscription</StyledTableCell>
                            <StyledTableCell>Sharing model</StyledTableCell>
                            <StyledTableCell>Terms</StyledTableCell>
                            <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {changedOrPendingSubscriptions.map(n => {
                            return (
                              <SubscriptionTableRow 
                                key={n.customerAppId}
                                subscription={n}
                                styledTableRow={StyledTableRow}
                                styledTableCell={StyledTableCell}
                                editable={false}
                                showModel={true}
                              />
                            );
                          })}
                        </TableBody>
                      </StyledTable>
                    </TableContainer>
                  </Grid>}

                  {showPreApprove === true &&
                  <Grid item xs={12}>
                    <Typography style={AppBindStyles.boldLabel}>{preAuthorizationMessage}</Typography>
                  </Grid>}
                </Grid>
              );
            }
          }
          if (!displayOnly) {
            return <div><LoadingSpinner/></div>
          }
        }
      }
      />
    );
  }

  /**
   * To move to the next step, we'll call the passed props handleNext function.
   * However, there is an edge case to persist the pre-approve subscription value.
   * If a non-null project is passed, we save pre-approval on the project.  Otherwise
   * we save it on the account.
   * 
   * @param {*} pendingSubscriptionCount 
   * @param {*} theProject
   * @param {*} theAccount 
   */
  handleNext(pendingSubscriptionCount, theProject, theAccount) {

    if (theProject == null) {
      // save on the account
      if (this.state.rPreApproveSubs !== null && theAccount.preApproveSubs !== 1) {
        //this value was changed.  Update the account and move to next step
        //only due this once (ie once the customer has accepted, the reseller does not see the checkbox and this should not happen)
        AccountPreApproveUpdateMutation.commit(theAccount.accountId, this.state.rPreApproveSubs)
        .then(response => {
          this.setState({showSpinner: false, showAllSubscriptions: false});    
        })
        .catch(error => {
          // this should never happen
          this.setState({showSpinner: false, showAllSubscriptions: false});    
        });
      } else {
        this.setState({showSpinner: false, showAllSubscriptions: false});    
      }
    } else {
      // save on the project
      if (this.state.rPreApproveSubs !== null && theProject.preApproveSubs !== 1) {
        //this value was changed.  Update the account and move to next step
        //only due this once (ie once the customer has accepted, the reseller does not see the checkbox and this should not happen)
        ProjectPreApproveUpdateMutation.commit(theProject.projectId, this.state.rPreApproveSubs)
        .then(response => {
          this.setState({showSpinner: false, showAllSubscriptions: false});    
        })
        .catch(error => {
          // this should never happen
          this.setState({showSpinner: false, showAllSubscriptions: false});    
        });
      } else {
        this.setState({showSpinner: false, showAllSubscriptions: false});    
      }
    }

    this.props.handleNext(pendingSubscriptionCount);
  }

  /**
   * This is passed to the AppAutosuggest component.  When the autosuggest component is used to select
   * a subscription, it calls this function to disable the next button.  Next is re-enabled when cancelling
   * the app creation.
   */
  setDisableNext = (value) => {
    if (value === true || value === false) {
      this.setState({nextDisabled: value});
    }
  }

  /**
   * Add subscription is passed to the AppAutosuggest component.  When a subscription is selected there,
   * that component calls this passed addSubscription function to add the subscription to the set
   * of pending subscriptions.
   * 
   * @param {*} cApp 
   */
  addSubscription = (cApp) => {
 
    const { callPartnerApi } = this.props.auth;
    let fetchCount = this.state.fetchCount;
    fetchCount++;

    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    //determine proper information to finally put on virtual card.  Start with provisioner's address as default
    let billingFirstName  = this.state.pbcUser.firstName;
    let billingLastName   = this.state.pbcUser.lastName;
    let billingEmail      = this.state.pbcUser.email;
    let billingPhone      = this.state.pbcUser.phone;
    let billingAddress1   = this.state.pbcUser.address1;
    let billingAddress2   = this.state.pbcUser.address2;
    let billingCity       = this.state.pbcUser.city;
    let billingState      = this.state.pbcUser.state;
    let billingCountry    = this.state.pbcUser.country;
    let billingPostalCode = this.state.pbcUser.postalCode;
    
    let usingAccount = this.state.bcUser.usingAccountId;
    if (usingAccount === abEnvironment.getUserAttribute('a_accountName')) {
      //nothing to do here.  reseller/provisioner is buying for themselves so address above is correct.
    } else {
    
      //use the customer info if present
      billingFirstName = this.state.bcUser.firstName;
      billingLastName = this.state.bcUser.lastName;
      billingEmail = this.state.bcUser.email;
      billingPhone = this.state.bcUser.phone;

      // If the customer address exists now, show it.
      // Otherwise, keep the reseller address for now.
      // it may be changed when customer adds funding source.
      if (this.state.bcUser.address1   != null && this.state.bcUser.address1   !== '' &&
          this.state.bcUser.city       != null && this.state.bcUser.city       !== '' &&
          this.state.bcUser.country    != null && this.state.bcUser.country    !== '' &&
          this.state.bcUser.postalCode != null && this.state.bcUser.postalCode !== '' ) {
        billingAddress1   = this.state.bcUser.address1;
        billingAddress2   = this.state.bcUser.address2;
        billingCity       = this.state.bcUser.city;
        billingState      = this.state.bcUser.state;
        billingCountry    = this.state.bcUser.country;
        billingPostalCode = this.state.bcUser.postalCode;
      }
    }

    this.setState({ saveStatus: "saving"});

    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');
    let provisioningAccountName = abEnvironment.getUserAttribute('a_accountName');

    // Need bind card user state data available within closure.
    let bcUser = this.state.bcUser;
    let usingAccountId = bcUser.usingAccountId;
    let usingAccountName = bcUser.accountName;

    let m_resp = null;
    let returnedAppId = null;
    let returnedCustomerAppId = null;
  
    // Initiate promise chain.
    Promise.resolve()  
    .then(() => {
      // Call the server side provisioning
      let aPjwt = abEnvironment.getPjwt();

      let payload = {
        operation: "createSubscriptionForProject",
        params: {
          pJwt:                aPjwt,
          accountId:           usingAccountId,
          accountName:         usingAccountName,
          pAccountId:          provisioningAccountId,
          pAccountName:        provisioningAccountName,
          projectId:           projectId,
          appId:               cApp.appId,
          appAccountId:        cApp.accountId,
          appStatus:           cApp.appStatus,
          appName:             cApp.appName,
          appSku:              cApp.appSku,
          appUrl:              cApp.appUrl,
          signupUrl:           cApp.signupUrl,
          appLogo:             cApp.appLogo,
          description:         cApp.description,
          notes:               cApp.notes,
          appType:             cApp.appType,
          parentAppId:         cApp.parentAppId,
          oemAppId:            cApp.oemAppId,
          atId:                cApp.atId,
          appSkuPrice:         cApp.appSkuPrice,
          appMarkupAbsolute:   cApp.appMarkupAbsolute,
          appMarkupPercentage: cApp.appMarkupPercentage,
          wholesaleDiscountPercentage: cApp.wholesaleDiscountPercentage,
          jitFloat:            0,
          marqetaMid:          cApp.marqetaMid,
          marqetaMname:        cApp.marqetaMname,
          maxCharge:           cApp.maxCharge,
          paymentOrder:        cApp.paymentOrder,
          passFees:            cApp.passThroughFees,
          showSubscription:    cApp.showSubscription,
          bcUserId:            bcUser.userId,
          bcLastName:          billingLastName,
          bcFirstName:         billingFirstName,
          bcEmail:             billingEmail,
          bcPhone:             billingPhone,
          bcAddress1:          billingAddress1,
          bcAddress2:          billingAddress2,
          bcCity:              billingCity,
          bcState:             billingState,
          bcCountry:           billingCountry,
          bcPostalCode:        billingPostalCode,
          soEntered:           1
        }
      };

      return callPartnerApi('appbind/generic',
                            'POST',
                              payload);
    })
    .then((response) => {
      return response.json();
    })
    .then((jresp) => {
      m_resp = jresp; // For trace purposes

      // - the return from the new flow is on a data attribute
      //set appId if a new one was created
      returnedAppId = jresp.data.appId;
      returnedCustomerAppId = jresp.data.customerAppId;

      //TODO - what does this do
      let subsCount = parseInt(abEnvironment.getUserAttribute("subs_count"), 10) + 1;
      abEnvironment.setUserAttribute("subs_count", subsCount);

      if (returnedCustomerAppId)
      {
        this.setState({
          appId: returnedAppId,
          createdCustomerAppId: returnedCustomerAppId,
          saveStatus: 'success',
          saveMessage: 'Subscription created!',
          fetchCount: fetchCount,
        });
      }
      else
      {
        this.setState({
          saveStatus: 'error',
          saveMessage: 'Unable to create the subscription: ' + m_resp
        });
      }
    }).then(() => {
      //every time a subscription is added, we set the latest_budget_approved off.  This looks a little funky but inside this budget update
      //it will always set budget approved off if called.
      if (returnedCustomerAppId)
      {
        if (projectId != null) {
          return RequestedBudgetProjectUpdateMutation.commit(projectId, 0);
        } else {
          return RequestedBudgetAccountUpdateMutation.commit(usingAccountId, null, null, null, null, null, null, null, null, null, null, null);
        }
      }
      return null;

    }).then((theRet) => {
      this.props.forceParentQuery();
    }).catch((error) => {
      this.setState({
        saveStatus: 'error',
        saveMessage: 'There was a problem creating the subscription.  Please contact support.'
      });
    });

  };

  /**
   * Add auth-only subscription is passed to the AppAutosuggest component.  When a subscription is selected there,
   * that component calls this passed function to add the subscription to the set
   * of pending auth-only subscriptions.
   * 
   * @param {*} cApp 
   */
  addAuthOnlySubscription = (cApp, taskData) => {
 
    const { callPartnerApi } = this.props.auth;
    let fetchCount = this.state.fetchCount;
    fetchCount++;

    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    //determine proper information to finally put on virtual card.  Start with provisioner's address as default
    let billingFirstName  = this.state.pbcUser.firstName;
    let billingLastName   = this.state.pbcUser.lastName;
    let billingEmail      = this.state.pbcUser.email;
    let billingPhone      = this.state.pbcUser.phone;
    let billingAddress1   = this.state.pbcUser.address1;
    let billingAddress2   = this.state.pbcUser.address2;
    let billingCity       = this.state.pbcUser.city;
    let billingState      = this.state.pbcUser.state;
    let billingCountry    = this.state.pbcUser.country;
    let billingPostalCode = this.state.pbcUser.postalCode;
    
    let usingAccount = this.state.bcUser.usingAccountId;
    if (usingAccount === abEnvironment.getUserAttribute('a_accountName')) {
      //nothing to do here.  reseller/provisioner is buying for themselves so address above is correct.
    } else {
    
      //use the customer info if present
      billingFirstName = this.state.bcUser.firstName;
      billingLastName = this.state.bcUser.lastName;
      billingEmail = this.state.bcUser.email;
      billingPhone = this.state.bcUser.phone;

      // If the customer address exists now, show it.
      // Otherwise, keep the reseller address for now.
      // it may be changed when customer adds funding source.
      if (this.state.bcUser.address1   != null && this.state.bcUser.address1   !== '' &&
          this.state.bcUser.city       != null && this.state.bcUser.city       !== '' &&
          this.state.bcUser.country    != null && this.state.bcUser.country    !== '' &&
          this.state.bcUser.postalCode != null && this.state.bcUser.postalCode !== '' ) {
        billingAddress1   = this.state.bcUser.address1;
        billingAddress2   = this.state.bcUser.address2;
        billingCity       = this.state.bcUser.city;
        billingState      = this.state.bcUser.state;
        billingCountry    = this.state.bcUser.country;
        billingPostalCode = this.state.bcUser.postalCode;
      }
    }

    this.setState({ saveStatus: "saving"});

    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');
    let provisioningAccountName = abEnvironment.getUserAttribute('a_accountName');

    // Need bind card user state data available within closure.
    let bcUser = this.state.bcUser;
    let usingAccountId = bcUser.usingAccountId;
    let usingAccountName = bcUser.accountName;

    let m_resp = null;
    let returnedAppId = null;
    let returnedCustomerAppId = null;
  
    // Initiate promise chain.
    Promise.resolve()  
    .then(() => {
      // Call the server side provisioning
      let aPjwt = abEnvironment.getPjwt();

      // console.log('createAuthOnlySubscriptionForProject passing paymentOrder ' + cApp.paymentOrder);

      let payload = {
        operation: "createAuthOnlySubscriptionForProject",
        params: {
          pJwt:                aPjwt,
          accountId:           usingAccountId,
          accountName:         usingAccountName,
          pAccountId:          provisioningAccountId,
          pAccountName:        provisioningAccountName,
          projectId:           projectId,
          appId:               cApp.appId,
          appAccountId:        cApp.accountId,
          appStatus:           cApp.appStatus,
          appName:             cApp.appName,
          appSku:              cApp.appSku,
          appUrl:              cApp.appUrl,
          signupUrl:           cApp.signupUrl,
          appLogo:             cApp.appLogo,
          description:         cApp.description,
          notes:               cApp.notes,
          appType:             cApp.appType,
          parentAppId:         cApp.parentAppId,
          oemAppId:            cApp.oemAppId,
          atId:                cApp.atId,
          appSkuPrice:         cApp.appSkuPrice,
          appMarkupAbsolute:   cApp.appMarkupAbsolute,
          appMarkupPercentage: cApp.appMarkupPercentage,
          wholesaleDiscountPercentage: cApp.wholesaleDiscountPercentage,
          jitFloat:            0,
          marqetaMid:          cApp.marqetaMid,
          marqetaMname:        cApp.marqetaMname,
          maxCharge:           cApp.maxCharge,
          paymentOrder:        cApp.paymentOrder,
          passFees:            cApp.passThroughFees,
          showSubscription:    cApp.showSubscription,
          bcUserId:            bcUser.userId,
          bcLastName:          billingLastName,
          bcFirstName:         billingFirstName,
          bcEmail:             billingEmail,
          bcPhone:             billingPhone,
          bcAddress1:          billingAddress1,
          bcAddress2:          billingAddress2,
          bcCity:              billingCity,
          bcState:             billingState,
          bcCountry:           billingCountry,
          bcPostalCode:        billingPostalCode,
          soEntered:           1,
          useEmail:            (cApp.useEmail == null) ? '' : cApp.useEmail,
          subPassword:         null,
          initiatedBy:         0,
          taskType:            cApp.taskType,
          taskData:            taskData,
          instructionTitle:    cApp.instructionTitle,
          instructionText:     cApp.instructionText,
          linkUrl:             cApp.linkUrl,
          linkName:            cApp.linkName
        }
      };

      return callPartnerApi('appbind/generic',
                            'POST',
                              payload);
    })
    .then((response) => {
      return response.json();
    })
    .then((jresp) => {
      m_resp = jresp; // For trace purposes

      // - the return from the new flow is on a data attribute
      //set appId if a new one was created
      returnedAppId = jresp.data.appId;
      returnedCustomerAppId = jresp.data.customerAppId;

      //TODO - what does this do
      let subsCount = parseInt(abEnvironment.getUserAttribute("subs_count"), 10) + 1;
      abEnvironment.setUserAttribute("subs_count", subsCount);

      if (returnedCustomerAppId)
      {
        this.setState({
          appId: returnedAppId,
          createdCustomerAppId: returnedCustomerAppId,
          saveStatus: 'success',
          saveMessage: 'Subscription created!',
          fetchCount: fetchCount,
        });
      } else {
        this.setState({
          saveStatus: 'error',
          saveMessage: 'Unable to create the subscription: ' + m_resp
        });
      }
    }).then(() => {
      //every time a subscription is added, we set the latest_budget_approved off.  This looks a little funky but inside this budget update
      //it will always set budget approved off if called.
      if (returnedCustomerAppId)
      {
        if (projectId != null) {
          return RequestedBudgetProjectUpdateMutation.commit(projectId, 0);
        } else {
          return RequestedBudgetAccountUpdateMutation.commit(usingAccountId, null, null, null, null, null, null, null, null, null, null, null);
        }
      }
      return null;

    }).then((theRet) => {
      this.props.forceParentQuery();
    }).catch((error) => {
      this.setState({
        saveStatus: 'error',
        saveMessage: 'There was a problem creating the subscription.  Please contact support.'
      });
    });
  };

  /**
   * Delete a subscription.  There is an edge case to check here - if a customer expense wallet is being set up but there
   * are no customer paid subscriptions, pending set up should be cleared out.
   */
  deleteSubscription = (theAccount, theProject, customerPaidCount, resellerPaidCount) => {
    // T
    let customerApp = this.state.subscriptionToDelete;
    if (customerApp != null) {
      // Put logic in a promise chain
      Promise.resolve()  
      .then(() => {  
        // Check the customer budget edge case
        if ((customerApp.paymentOrder === 0 || customerApp.paymentOrder === 1) && customerPaidCount === 1)  {
          if (theProject != null) {
            let customerWallet = theProject.walletTByCustomerWalletId;
            if (customerWallet.rAutoTopupAmount != null && (customerWallet.autoTopupAmount == null || Number(customerWallet.autoTopupAmount) === 0)) {
              //console.log('Edge case (wallet) - no paid subscriptions, so do not set up a new wallet topup');
              return RequestedBudgetWalletUnsetMutation.commit(customerWallet.walletId)
            }
          } else {
            if (theAccount.rAutoTopupAmount != null && (theAccount.autoTopupAmount == null || Number(theAccount.autoTopupAmount) === 0)) {
              //console.log('Edge case (account) - no paid subscriptions, so do not set up a new wallet topup');
              return RequestedBudgetAccountUnsetMutation.commit(theAccount.accountId);
            }
          }
        } else {
          return null;
        }
      }).then((theRet) => {
        let customerAppId = customerApp.customerAppId;
        return DeletePendingSubscriptionMutation.commit(customerAppId);
      }).then((response) => {
        let fetchCount = this.state.fetchCount;
        fetchCount++;
        this.setState({ fetchCount: fetchCount });
        this.props.forceParentQuery();
      })
      .catch(error => {
        // console.log('Got error: ' + error);
      });
    }
  }
}

StepSubscriptions.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(StepSubscriptions);
